<script setup lang="ts">
import { useAuthStore } from '#modules/auth/store/auth';
import avatarPlaceholder from '@/assets/img/avatar-placeholder.svg';
import { AppAvatar, AppButton, AppLogo, NavbarMenu } from '@/components';
import { useMobileStore } from '@/store/mobile';
import { storeToRefs } from 'pinia';
import { useDisplay } from 'vuetify';
import LogoutIcon from '~/assets/img/icon/logout.svg';
import AppConfirmationDialog from '~/components/AppConfirmationDialog.vue';
import { AppTypography } from '~/components/base';
import { useNotification } from '~/composables/useNotification';
import { useNotificationsStore } from '~/store/notifications';

const router = useRouter();
const route = useRoute();
const display = useDisplay();

const tabletAndLessSize = computed(() => {
  return display.width.value <= 768;
});

const authStore = useAuthStore();
const { user, hasCompletedOnboarding } = storeToRefs(authStore);
const isLoggedIn = computed(() => !!user.value);

// Initialize notifications store and composable
const notificationsStore = useNotificationsStore();
const { getNotifications } = useNotification();
const { notifications, unreadCount } = storeToRefs(notificationsStore);

// Check if the first notification is unread (for dot indicator)
const isFirstNotificationUnread = computed(() => {
  return notifications.value.length > 0 && notifications.value[0]?.read === false;
});

// Ref for bell button positioning
const bellButtonRef = ref<any>();

// Show Continue Onboarding button only when:
// 1. Onboarding is not completed AND
// 2. User is NOT currently on an onboarding page or verify page
const showContinueOnboarding = computed(() => {
  const isOnOnboardingFlow = route.path.startsWith('/onboarding') || route.path === '/verify';
  return !hasCompletedOnboarding.value && !isOnOnboardingFlow;
});

const mobileStore = useMobileStore();
const { showMenu } = storeToRefs(mobileStore);
const showWebMenu = ref(false);
const logoutDialog = ref<boolean>(false);

const mobileIcon = computed((): string => {
  return showMenu.value ? 'mdi-close' : 'mdi-menu';
});

const { signOut } = useAuth();

const handleLogout = async () => {
  await signOut({ callbackUrl: '/login' });
  logoutDialog.value = false;
  authStore.logout();
};

const handleOpenLogoutDialog = () => {
  logoutDialog.value = true;
};

const handleToggle = () => {
  if (!hasCompletedOnboarding.value) {
    return;
  }

  showMenu.value = !showMenu.value;
};

const handleLogin = () => {
  router.push('/login');
};

const handleSignup = () => {
  router.push('/register');
};

const handlePostListing = () => {
  router.push('/jobs/create');
};

const handleNotifications = async () => {
  // Set default top-right positioning
  notificationsStore.setPosition('top-right');

  // Fetch notifications from API and sync with store
  await getNotifications();

  // Open drawer
  notificationsStore.openDrawer();
};

const handleContinueOnboarding = () => {
  router.push('/onboarding/goal-selection');
};

const handleLogoClick = () => {
  if (!hasCompletedOnboarding.value) {
    return;
  }

  router.push('/');
};

// Sticky navbar with scroll behavior
const scrolled = ref(false);

const handleScroll = () => {
  scrolled.value = window.scrollY > 10;
};

onMounted(() => {
  window.addEventListener('scroll', handleScroll);
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
});
</script>

<template>
  <v-app-bar elevation="0" height="72" app class="the-navbar" :class="{ 'the-navbar--scrolled': scrolled }">
    <template v-slot:prepend>
      <div
        @click="handleLogoClick"
        class="d-flex align-center justify-center"
        :class="{ 'logo-wrapper': $route.path !== '/' }"
      >
        <AppLogo width="183px" height="24px" />
      </div>
    </template>

    <!-- Desktop Actions - Logged Out -->
    <div v-if="!isLoggedIn && !tabletAndLessSize" class="nav-actions mr-4">
      <AppButton variant="flat" color="white" class="login-btn" size="small" @click="handleLogin"> Login</AppButton>
      <AppButton variant="flat" color="primary" class="signup-btn" size="small" @click="handleSignup">
        Sign Up
      </AppButton>
    </div>

    <!-- Desktop Actions - Logged In -->
    <div v-if="isLoggedIn && !tabletAndLessSize" class="nav-actions nav-actions--logged-in">
      <AppButton
        v-if="hasCompletedOnboarding"
        variant="flat"
        color="primary"
        class="post-listing-btn"
        size="small"
        @click="handlePostListing"
      >
        <v-icon start>mdi-plus</v-icon>
        Post a Listing
      </AppButton>

      <v-divider vertical color="#414156" opacity="1" thickness="1" />

      <!-- Continue Onboarding Button -->
      <AppButton
        v-if="showContinueOnboarding"
        variant="flat"
        color="primary"
        class="continue-onboarding-btn"
        size="small"
        @click="handleContinueOnboarding"
      >
        <v-icon start>mdi-chevron-right</v-icon>
        Continue Onboarding
      </AppButton>

      <!-- Notification Bell -->
      <div class="notification-bell-container" v-if="hasCompletedOnboarding">
        <!-- Bell Button -->
        <AppButton
          ref="bellButtonRef"
          variant="text"
          color="white"
          class="notification-btn"
          rounded
          @click="handleNotifications"
        >
          <icon name="ewc:notification" size="24" class="text-white" />
          <!-- Unread Dot Indicator -->
          <span v-if="isFirstNotificationUnread" class="notification-dot"></span>
        </AppButton>
        <!-- Notification Badge -->
        <div v-if="unreadCount > 0" class="notification-badge">
          {{ unreadCount > 99 ? '99+' : unreadCount }}
        </div>
      </div>

      <!-- User Profile Menu -->
      <v-menu
        v-model="showWebMenu"
        v-if="hasCompletedOnboarding"
        width="352px"
        :offset="24"
        location="bottom end"
        class="the-navbar__user-profile-menu"
      >
        <template #activator="{ props }">
          <div class="user-profile mr-4" v-bind="props">
            <AppAvatar
              :src="(user?.avatar_permanent_url || user?.avatar || avatarPlaceholder) as string"
              :size="28"
              class="user-profile__avatar"
            />
            <AppTypography element="span" type="body-1" class="user-profile__name font-weight-semibold">
              {{ user?.full_name || 'User' }}
            </AppTypography>
            <v-icon color="white" size="x-large">mdi-menu-down</v-icon>
          </div>
        </template>
        <NavbarMenu class="the-navbar__menu" @handle-open-logout-dialog="handleOpenLogoutDialog" />
      </v-menu>
    </div>

    <!-- Mobile Menu Button -->
    <AppButton
      v-if="tabletAndLessSize"
      variant="text"
      :icon="mobileIcon"
      color="white"
      class="the-navbar__mobile"
      @click="handleToggle"
    />

    <AppConfirmationDialog
      v-model="logoutDialog"
      :icon="LogoutIcon"
      title="Log out?"
      description="Are you sure you want to<br>log out of your account?"
      @button-action="handleLogout"
      @close-action="logoutDialog = false"
      btnColor="#F10021"
      btn-text="Log out"
      cancelBtnText="Stay logged in"
      :width="391"
      cancelBtn
    />
  </v-app-bar>

  <!-- Mobile Navigation Drawer -->
  <v-navigation-drawer
    v-if="hasCompletedOnboarding"
    v-model="showMenu"
    location="right"
    temporary
    width="370"
    class="the-navbar__mobile-drawer"
  >
    <NavbarMenu @handle-open-logout-dialog="handleOpenLogoutDialog" />
  </v-navigation-drawer>
</template>

<style lang="scss" scoped>
@use '@/assets/scss/partials/colors' as *;

:deep(.v-btn.app-btn.v-btn--variant-text:hover) {
  box-shadow: none;
}

.nav-link {
  position: relative;
  font-size: 16px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0;
  color: white;
  padding-left: 16px;
  text-decoration: none;
}

.nav-link--active {
  font-weight: 600;
  color: rgb(var(--v-theme-primary));
}

.nav-link--active::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: rgb(var(--v-theme-primary));
}

:deep(.v-toolbar__content) {
  max-width: 1440px;
  margin: auto;
}

.the-navbar {
  position: fixed !important;
  top: 0 !important;
  width: 100% !important;
  background-color: $dark-navy !important;
  border-bottom: 1px solid $border-secondary;
  border-radius: 0 0 16px 16px !important;
  padding: 0 16px;
  z-index: 1000 !important; // Ensure navbar stays above all content
  transition: all 0.3s ease-in-out;

  .logo-wrapper {
    cursor: pointer;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 0.8;
    }
  }

  &__profile {
    background-color: transparent !important;
  }

  &__mobile {
    display: none;
    color: white !important;
  }

  &__mobile-menu {
    display: none;
    position: fixed;
    top: 72px;
    left: 0;
    width: 100%;
    height: calc(100dvh - 72px); // Use dvh for iOS Safari
    height: calc(100vh - 72px); // Fallback for browsers that don't support dvh
    z-index: 999; // Just below navbar
    background-color: white;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch; // Smooth scrolling on iOS

    .v-list {
      height: 100%;
      background-color: white;
      color: rgba(0, 0, 0, 0.87);
    }
  }

  // Optional: Add shadow when scrolled for better visual feedback
  &--scrolled {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
}

.nav-actions {
  display: flex;
  align-items: center;
  gap: 16px;

  &--logged-in {
    gap: 24px;
  }
}

.login-btn,
.signup-btn,
.post-listing-btn,
.continue-onboarding-btn {
  min-width: auto !important;
  padding: 8px 16px !important;
  height: 40px !important;
  font-size: 14px !important;
  font-weight: 600 !important;
}

.notification-bell-container {
  position: relative;
  display: inline-block;
}

.notification-btn {
  min-width: 40px !important;
  max-width: 40px !important;
  width: 40px !important;
  height: 40px !important;

  border-radius: 50% !important;
  background-color: rgba(255, 255, 255, 0.1) !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  position: relative !important;

  &:hover {
    background-color: rgba(255, 255, 255, 0.15) !important;
  }
}

// Unread dot indicator - shows when first notification is unread
.notification-dot {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 8px;
  height: 8px;
  background-color: #f10021;
  border-radius: 50%;
  border: 1.5px solid $dark-navy;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  pointer-events: none;
  z-index: 1;
}

.notification-badge {
  display: none !important; // Hidden by default
  position: absolute;
  top: -4px;
  right: -4px;
  background-color: #f10021; // Red color for unread notifications
  color: white;
  border-radius: 10px;
  min-width: 20px;
  height: 20px;
  font-size: 10px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
  border: 2px solid $dark-navy;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  pointer-events: none; // Don't interfere with button clicks
  z-index: 1;
}

.user-profile {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  height: 45px;
  border-radius: 20px;
  background-color: rgba(255, 255, 255, 0.1);
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(255, 255, 255, 0.15);
  }

  &__avatar {
    flex-shrink: 0;
  }

  &__name {
    color: white;
    font-family: 'Noto Sans';
    font-size: 14px !important;
    line-height: 1;
    white-space: nowrap;
  }
}

@media (max-width: 768px) {
  .the-navbar {
    border-bottom: 1px solid $border-secondary;

    &__mobile {
      display: block;
    }
  }
}

// Mobile Navigation Drawer Styles
.the-navbar__mobile-drawer {
  top: 72px !important;
  height: calc(100vh - 72px) !important;
  max-height: calc(100vh - 72px) !important;

  .v-navigation-drawer__content {
    display: flex;
    flex-direction: column;
  }
}
</style>

<style lang="scss">
@use '@/assets/scss/partials/colors' as *;

// Shadow for TheNavbar User Profile Menu
// Must be global (non-scoped) because v-menu uses portal rendering
// Only affects this component via the unique class name
.the-navbar__user-profile-menu {
  .v-overlay__content {
    box-shadow:
      0 8px 32px rgba(0, 0, 0, 0.35),
      0 4px 16px rgba(0, 0, 0, 0.2) !important;
  }
}
</style>
